import React, { useEffect, useState } from 'react';
import { Download, Edit, Eye, Plus, Trash2 } from 'lucide-react';
import { employeeLicenseApi } from '../services/api';
import type { CreateEmployeeLicenseRequest, EmployeeLicense } from '../types';
import { HEALTHCARE_LICENSE_TYPES, getLicenseExpiryStatus, LICENSE_STATUS_STYLES } from '../constants/healthcareLicenseTypes';
import CertificatePreviewModal from './CertificatePreviewModal';
import { triggerBlobDownload } from '../utils/certificateFiles';
import { useToast } from './Toast';
import { appConfirm } from '../context/SystemDialogContext';
import { formatCompanyDate } from '../utils/companyDateFormat';

type Props = {
  employeeId: number;
  licenses?: EmployeeLicense[];
  onRefresh?: () => void | Promise<void>;
  highlightLicenseId?: number | null;
  onChanged?: (licenses: EmployeeLicense[]) => void;
  readOnly?: boolean;
};

const EmployeeLicensesTab: React.FC<Props> = ({
  employeeId,
  licenses,
  onRefresh,
  highlightLicenseId,
  onChanged,
  readOnly = false,
}) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState<EmployeeLicense | null>(null);
  const [localLicenses, setLocalLicenses] = useState<EmployeeLicense[]>(licenses ?? []);
  const [previewLicense, setPreviewLicense] = useState<EmployeeLicense | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(!licenses);
  const [formData, setFormData] = useState<CreateEmployeeLicenseRequest>({
    employeeId,
    licenseType: '',
    licenseNumber: '',
    expiryDate: '',
  });

  useEffect(() => {
    if (licenses) setLocalLicenses(licenses);
  }, [licenses]);

  useEffect(() => {
    if (licenses) return;
    let cancelled = false;
    employeeLicenseApi
      .getByEmployeeId(employeeId)
      .then((rows) => {
        if (!cancelled) setLocalLicenses(rows);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load licenses.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // toast is a stable module hook; omit from deps to avoid reload loops
  }, [employeeId, licenses]);

  const refreshLicenses = async (notify = false) => {
    const refreshedLicenses = await employeeLicenseApi.getByEmployeeId(employeeId);
    setLocalLicenses(refreshedLicenses);
    await onRefresh?.();
    if (notify) onChanged?.(refreshedLicenses);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingLicense) {
        await employeeLicenseApi.update(editingLicense.id, {
          licenseType: formData.licenseType,
          licenseNumber: formData.licenseNumber,
          expiryDate: formData.expiryDate,
        });
      } else {
        await employeeLicenseApi.create(formData);
      }
      setShowModal(false);
      setEditingLicense(null);
      setFormData({ employeeId, licenseType: '', licenseNumber: '', expiryDate: '' });
      await refreshLicenses(true);
      toast.success(editingLicense ? 'License updated successfully!' : 'License added successfully!');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save license. Please try again.');
    }
  };

  const handleEdit = (license: EmployeeLicense) => {
    setEditingLicense(license);
    setFormData({
      employeeId,
      licenseType: license.licenseType ?? '',
      licenseNumber: license.licenseNumber,
      expiryDate: license.expiryDate.split('T')[0],
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (await appConfirm('Are you sure you want to delete this license?', { danger: true, confirmLabel: 'Delete' })) {
      try {
        await employeeLicenseApi.delete(id);
        await refreshLicenses(true);
        toast.success('License deleted successfully!');
      } catch {
        toast.error('Failed to delete license. Please try again.');
      }
    }
  };

  const downloadCertificate = async (license: EmployeeLicense) => {
    try {
      setDownloadingId(license.id);
      const blob = await employeeLicenseApi.downloadCertificate(license.id, 'attachment');
      triggerBlobDownload(blob, license.certificateFileName || `${license.licenseType || 'certificate'}.pdf`);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Download failed.');
    } finally {
      setDownloadingId(null);
    }
  };

  const uploadCertificate = async (licenseId: number, file: File) => {
    try {
      await employeeLicenseApi.uploadCertificate(licenseId, file);
      await refreshLicenses(true);
      toast.success('Certificate uploaded — employee and supervisors have been notified.');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Failed to upload certificate.');
    }
  };

  if (loading) {
    return <p className="text-sm text-neutral-500 py-8 text-center">Loading licenses…</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold text-neutral-800">Compliance &amp; Licenses</h2>
          <p className="text-sm text-neutral-500 mt-1">
            {readOnly
              ? 'Review expiry dates and certificates. Take over the Inbox item to make changes.'
              : 'Track expiry dates and review uploaded renewal certificates.'}
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => {
              setEditingLicense(null);
              setFormData({ employeeId, licenseType: '', licenseNumber: '', expiryDate: '' });
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add License
          </button>
        )}
      </div>

      {localLicenses.length === 0 ? (
        <p className="text-neutral-500">No licenses found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">License Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">License Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Expiry Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Certificate</th>
                {!readOnly && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {localLicenses.map((license) => {
                const status = getLicenseExpiryStatus(license.expiryDate);
                const styles = LICENSE_STATUS_STYLES[status.tone];
                const highlighted = highlightLicenseId != null && license.id === highlightLicenseId;
                return (
                  <tr key={license.id} className={highlighted ? 'bg-amber-50' : undefined}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{license.licenseType || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{license.licenseNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{formatCompanyDate(license.expiryDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${styles.badge}`}>{status.label}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-700">
                      {license.certificateFileName ? (
                        <div className="flex flex-col gap-1.5">
                          <span className="truncate max-w-[200px]" title={license.certificateFileName}>
                            {license.certificateFileName}
                          </span>
                          {!readOnly && (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setPreviewLicense(license)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              Preview
                            </button>
                            <button
                              type="button"
                              disabled={downloadingId === license.id}
                              onClick={() => void downloadCertificate(license)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium disabled:opacity-50"
                            >
                              <Download className="h-3.5 w-3.5" />
                              {downloadingId === license.id ? 'Downloading…' : 'Download'}
                            </button>
                            <label className="inline-flex items-center gap-1 text-neutral-600 hover:text-neutral-800 text-xs font-medium cursor-pointer">
                              Replace
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (file) void uploadCertificate(license.id, file);
                                }}
                              />
                            </label>
                          </div>
                          )}
                        </div>
                      ) : readOnly ? (
                        <span className="text-neutral-400">None uploaded</span>
                      ) : (
                        <label className="text-blue-600 hover:text-blue-800 cursor-pointer underline">
                          Upload
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = '';
                              if (file) void uploadCertificate(license.id, file);
                            }}
                          />
                        </label>
                      )}
                    </td>
                    {!readOnly && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button type="button" onClick={() => handleEdit(license)} className="text-blue-600 hover:text-blue-900 mr-4">
                          <Edit className="h-4 w-4 inline" />
                        </button>
                        <button type="button" onClick={() => handleDelete(license.id)} className="text-red-600 hover:text-red-900">
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && !readOnly && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{editingLicense ? 'Edit License' : 'Add License'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">License Type</label>
                <input
                  type="text"
                  list="healthcare-license-types"
                  value={formData.licenseType ?? ''}
                  onChange={(e) => setFormData({ ...formData, licenseType: e.target.value })}
                  placeholder="e.g. Medical Indemnity"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <datalist id="healthcare-license-types">
                  {HEALTHCARE_LICENSE_TYPES.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">License Number *</label>
                <input
                  type="text"
                  required
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Expiry Date *</label>
                <input
                  type="date"
                  required
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  {editingLicense ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CertificatePreviewModal
        licenseId={previewLicense?.id ?? null}
        fileName={previewLicense?.certificateFileName}
        title={previewLicense?.licenseType || undefined}
        onClose={() => setPreviewLicense(null)}
      />
    </div>
  );
};

export default EmployeeLicensesTab;
